import type { Element, Root } from 'hast';
import type { Plugin } from 'unified';
import { visit, SKIP } from 'unist-util-visit';

export type RehypeTruncateOptions = {
  characterLimit: number;
  showEllipsis?: boolean;
};

const areAllChildrenEmpty = (node: Element): boolean => {
  return node.children.every((child) => {
    if (child.type === 'text') {
      return !child.value.trim();
    }

    if (child.type === 'element') {
      return areAllChildrenEmpty(child);
    }

    return true;
  });
};

const removeEmptyElements = (tree: Root) => {
  visit(tree, 'element', (node: Element, index, parent) => {
    const isContentEmpty =
      node.tagName !== 'hr' &&
      node.tagName !== 'br' &&
      (!node.children ||
        node.children.length === 0 ||
        areAllChildrenEmpty(node));

    if (isContentEmpty && parent && typeof index === 'number') {
      parent.children.splice(index, 1);
      return [SKIP, index];
    }
  });
};

const rehypeTruncate: Plugin<[RehypeTruncateOptions], Root> = (
  options: RehypeTruncateOptions
) => {
  const { characterLimit, showEllipsis = false } = options;

  return (tree) => {
    let count = 0;
    let done = false;

    visit(tree, 'text', (node, index, parent) => {
      if (done) {
        if (parent && typeof index === 'number') {
          parent.children.splice(index, 1);
          return [SKIP, index];
        }
        return;
      }

      const text = node.value;
      const remaining = characterLimit - count;

      if (text.length <= remaining) {
        count += text.length;
      } else {
        node.value = text.slice(0, remaining);

        if (showEllipsis) {
          node.value += '…';
        }

        count += remaining;
        done = true;
      }
    });

    removeEmptyElements(tree);
  };
};

export default rehypeTruncate;
